import { useEffect, useRef, useState } from 'react'

import { Badge, Icon, TextArea } from '@percent/lemonade'
import classes from './QuestionInput.module.scss'

// Kept within the backend's 1-4000 char message bound, so an over-long question is
// capped at the source rather than bouncing off a 400.
const DEFAULT_MAX_QUESTION_LENGTH = 1000

export type QuestionInputProps = Readonly<{
  value: string
  onChange: (value: string) => void
  onSubmit: () => void
  isLoading: boolean
  disabled?: boolean
  /** Lemonade "small" elevation — lifts a bottom-pinned input off the conversation. */
  elevated?: boolean
  /** Play the one-time gradient border sweep on mount (default true). */
  animateBorder?: boolean
  /** Delay (ms) before the border sweep starts (default 0). */
  animationDelayMs?: number
  placeholder: string
  /** Accessible label for the send button. */
  submitLabel: string
  /** Optional "Beta" pill copy; omitted → no badge. */
  betaLabel?: string
  maxLength?: number
  name?: string
  inputTestId?: string
  submitTestId?: string
}>

/**
 * The question entry box: a Lemonade `TextArea` framed as the input surface with a
 * dark circular send button pinned bottom-right. Fully controlled. Enter submits
 * (Shift+Enter for a newline); an empty submit shakes the box. On first mount a
 * gradient border sweeps around it once. All copy and testids are injected — this
 * component carries no product-specific strings.
 */
export function QuestionInput({
  value,
  onChange,
  onSubmit,
  isLoading,
  disabled = false,
  elevated = false,
  animateBorder = true,
  animationDelayMs = 0,
  placeholder,
  submitLabel,
  betaLabel,
  maxLength = DEFAULT_MAX_QUESTION_LENGTH,
  name = '',
  inputTestId,
  submitTestId,
}: QuestionInputProps) {
  const [isShaking, setIsShaking] = useState(false)
  const [isAnimating, setIsAnimating] = useState(false)
  const shakeTimerRef = useRef<ReturnType<typeof setTimeout>>()

  useEffect(() => () => clearTimeout(shakeTimerRef.current), [])

  useEffect(() => {
    if (!animateBorder) return undefined

    const startTimer = setTimeout(() => setIsAnimating(true), animationDelayMs)

    return () => clearTimeout(startTimer)
  }, [animateBorder, animationDelayMs])

  const inputDisabled = isLoading || disabled

  const handleSubmit = () => {
    if (!value.trim()) {
      setIsShaking(false)
      requestAnimationFrame(() => setIsShaking(true))
      shakeTimerRef.current = setTimeout(() => setIsShaking(false), 300)

      return
    }

    onSubmit()
  }

  const wrapperClass = [
    classes.borderWrapper,
    elevated ? classes.borderWrapperElevated : '',
    isAnimating ? classes.borderAnimate : '',
    isShaking ? classes.borderWrapperShaking : '',
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <div className={wrapperClass}>
      <div className={classes.inputInner}>
        <TextArea
          name={name}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && !event.shiftKey) {
              event.preventDefault()
              handleSubmit()
            }
          }}
          placeholder={placeholder}
          aria-label={placeholder}
          disabled={inputDisabled}
          className={classes.questionField}
          data-testid={inputTestId}
          maxLength={maxLength}
        />
        {betaLabel && (
          <span className={classes.betaBadge}>
            <Badge variant="interactive">{betaLabel}</Badge>
          </span>
        )}
        <button
          onClick={handleSubmit}
          disabled={inputDisabled}
          className={classes.sendBtn}
          type="button"
          data-testid={submitTestId}
          aria-label={submitLabel}
        >
          <Icon name="arrow-up" size={5} color="white" />
        </button>
      </div>
    </div>
  )
}
